import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const propagationPhasesCode = `// Visualizing the 3-Phase Propagation Model

const grand = document.getElementById('grandparent');
const parent = document.getElementById('parent');
const child = document.getElementById('child');

// 1. CAPTURING Phase (Top-Down: Window -&gt; Target)
grand.addEventListener('click', (e) => {
    console.log("Phase 1: Captured at Grandparent");
}, { capture: true });

// 2. TARGET Phase (At the element itself)
child.addEventListener('click', (e) => {
    console.log("Phase 2: Target reached at Child");
});

// 3. BUBBLING Phase (Bottom-Up: Target -&gt; Window)
parent.addEventListener('click', (e) => {
    console.log("Phase 3: Bubbled to Parent");
});`;

const stopPropagationCode = `// Controlling Flux: Stopping the Propagation

btn.addEventListener('click', (e) => {
    console.log("Button processing complete.");
    
    // 1. stopPropagation() - Prevents the event from reaching parents
    e.stopPropagation(); 
    
    // 2. stopImmediatePropagation() - Prevents propagation AND 
    // kills other listeners on THIS same element.
    e.stopImmediatePropagation();
});

// Logic hazard: excessive use of stopPropagation breaks 
// Global analytics (tracking clicks) and generic UI components.`;

const delegationPatternCode = `// The Delegation Architecture: One Listener, Infinite Children

const container = document.querySelector('.data-grid');

// OPTIMIZED: Handling actions for 10,000 rows with 1 listener
container.addEventListener('click', (e) => {
    // 1. Use .closest() to find the logical 'item' boundary
    const row = e.target.closest('.grid-row');
    const deleteBtn = e.target.closest('[data-action="delete"]');
    
    // 2. Ensure the click originated within a valid target
    if (deleteBtn && row) {
        console.log("Deleting record ID:", row.dataset.id);
        row.remove();
    }
});

// Performance Insight: 
// Memory usage = 1 callback (instead of 10,000)
// GC (Garbage Collection) = Zero overhead when rows are added/removed.`;

const delegationHelperCode = `// Architectural Pattern: The Delegation Factory

function createDelegator(rootSelector) {
    const root = document.querySelector(rootSelector);
    
    return {
        on(event, childSelector, callback) {
            root.addEventListener(event, (e) => {
                const target = e.target.closest(childSelector);
                // Ensure the target is actually inside our root
                if (target && root.contains(target)) {
                    callback.call(target, e, target);
                }
            });
        }
    };
}

// Usage
const menu = createDelegator('#main-menu');
menu.on('click', 'a.nav-link', (e, el) => {
    e.preventDefault();
    console.log("Navigating to:", el.href);
});`;

const JSEventDelegation = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Events & Handling</div>
                <h1>Propagation & Event Delegation</h1>
                <p className="hero-lead">
                    Master the high-performance patterns of browser events. Learn to
                    architect your applications using Event Delegation to reduce memory
                    overhead, understand the nuance of Capture vs. Bubble phases, and
                    control event flow with surgical precision.
                </p>
            </div>

            <div className="content-section">
                <h2>The Propagation Model: Capture and Bubble</h2>
                <p>
                    In the browser's Document Object Model, an event is not a localized incident; it is a
                    wave that travels through the tree hierarchy in three distinct architectural phases.
                    The journey begins with the **Capture Phase** (Phase 1), where the event trickles down
                    from the global `window` object to the target element, visiting every ancestor along
                    the way. This is followed by the **Target Phase** (Phase 2), where the event is
                    dispatched specifically to the element that triggered the interaction. Finally,
                    the event enters the **Bubbling Phase** (Phase 3), traveling back up the tree from
                    the target to the root. By default, 99% of web applications listen during the bubbling
                    phase, but understanding the capture phase is essential for advanced patterns
                    where a parent must intercept an event before any of its children can react
                    to it.
                </p>
                <p>
                    Why does this complex three-phase model exist? It provides a robust framework
                    for both global and local event management. Capturing allows for "Gatekeeper" logic,
                    such as a global keyboard shortcut manager that intercepts key presses before they
                    reach a text input. Bubbling, on the other hand, is the foundation of the
                    "Event Delegation" pattern, enabling parent elements to observe the activities of
                    their descendants. Most developers use bubbling because it is intuitiveclicking a
                    button inside a div feels like you are also clicking the div itself. Mastering the
                    mechanics of this flow transforms event handling from simple callbacks into
                    an orchestrated messaging system that respects the structural relationships
                    of your application's UI.
                </p>

                <CodeBlock code={propagationPhasesCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Event Delegation: Performance at Scale</h2>
                <p>
                    Event Delegation is the most important performance optimization in the
                    JavaScript event system. Imagine a data table with 10,000 rows, each
                    containing a "Delete" button. Attaching 10,000 individual event listeners
                    would consume a massive amount of RAM and slow down the initial rendering
                    of the page as the browser allocates memory for each callback pointer.
                    With event delegation, you attach exactly **one** listener to the parent
                    <code>&lt;table&gt;</code> or <code>&lt;body&gt;</code>. When any button is clicked, the event bubbles up
                    to the parent, where you inspect the `event.target` to identify
                    which button was clicked. This reduces memory usage by 99.9% while
                    making the application significantly more responsive on lower-end devices.
                </p>
                <p>
                    Another critical advantage of delegation is its relationship with dynamic
                    content. In modern "Auto-loading" or "Infinite Scroll" interfaces,
                    elements are added and removed from the DOM constantly. If you used
                    individual listeners, you would have to manually attach a new listener
                    every time a row is added and remove it when the row is deleted to
                    prevent memory leaks. With delegation, the listener stays on the
                    stable parent element; any new children added to the tree are
                    automatically picked up by the parent's logic without a single
                    line of extra code. This architectural pattern simplifies your
                    application's state management and creates a "set-it-and-forget-it"
                    interaction layer that scales gracefully alongside your data.
                </p>

                <CodeBlock code={delegationPatternCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Controlling Flux: Stopping Propagation</h2>
                <p>
                    There are scenarios where you specifically want to prevent a parent element from
                    noticing an event that occurred inside one of its children. The `stopPropagation()`
                    method is the tool for this, effectively "cutting the wire" during the bubbling
                    phase. This is commonly used in nested interactive componentsfor example, a
                    "Close" button inside a clickable "Card." Clicking the button should close the
                    card, but without `stopPropagation`, the click would bubble up and also trigger
                    the card's "Select" logic, leading to a confusing user experience where the
                    card is both closed and selected simultaneously. By stopping the flow, you
                    isolate interactions and ensure that components only react to the direct
                    actions intended for them.
                </p>
                <p>
                    However, senior engineers use `stopPropagation()` with extreme caution.
                    Stopping propagation can break global monitoring tools like Google Analytics
                    or heat-mapping software that relies on bubbling to track user engagement.
                    It can also disrupt generic UI libraries that listen to the `window` to close
                    dropdowns when a click occurs elsewhere. A safer alternative is often
                    to check the `event.target` in the parent and ignore the event if it
                    originated from a specific child, rather than silencing the signal entirely.
                    This "Inclusive Architecture" ensures that your internal component logic
                    doesn't have unintended side effects on the rest of the application's
                    observability and shared behaviors.
                </p>

                <CodeBlock code={stopPropagationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Advanced Strategy: Delegation Factories</h2>
                <p>
                    To keep your codebase clean, you should avoid repeating the logic for
                    target identification and `closest()` checks in every listener. Instead,
                    capture this logic in a **Delegation Factory** or a central event
                    management system. By creating a utility that takes a root element, a
                    selector, and a handler, you can implement delegators with the same
                    declarative ease as standard listeners. This abstraction layer handles
                    the complexity of ensuring the event target is actually a descendant of
                    the root and correctly binds the `this` context to the matched element.
                    This approach is not just about reducing lines of code; it is about
                    standardizing how interactions are handled across your entire platform,
                    making it easier for other developers to understand and maintain
                    the interaction layer.
                </p>

                <CodeBlock code={delegationHelperCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Scale:** Use delegation for containers with many similar interactive items.</li>
                    <li>**Stability:** Use delegation to handle dynamic elements that are added post-load.</li>
                    <li>**Specificity:** Use `e.target.closest()` to identify logical component boundaries.</li>
                    <li>**Interception:** Use <code>{'{ capture: true }'}</code> for high-priority global gatekeeper logic.</li>
                    <li>**Restraint:** Use `stopPropagation()` only when necessary to prevent logic overlaps.</li>
                    <li>**Memory:** Monitor listener count to avoid fragmentation in long-running apps.</li>
                    <li>**Abstraction:** Use delegation helpers to standardize interaction logic.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSEventDelegation;


